/**
 * RecommendationCard Component — Week 1 Day 2 rebuild
 *
 * Rich inline card matching the original-app AreaDetails aesthetic:
 * - Hero image (from Google Places Photos via placePhotosProvider)
 *   with gradient placeholder fallback when no photo available
 * - Rank badge + match score overlay
 * - Neighborhood name + description
 * - Auto-derived pill tags (Safe / Walkable / Good Schools / etc.)
 * - 3 metric sections: Schools, Rent, Commute  (Safety removed 2026-10-16)
 * - Thumbs up/down feedback (frontend state only — backend wiring Week 2)
 * - Collapsible "Why this neighborhood?" deep-dive
 *   with highlights, why-it-fits, and score breakdown
 *
 * All visual values reference design tokens in styles/tokens.css.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  submitRecommendationFeedback,
  clearRecommendationFeedback
} from '../../services/feedbackApi';
import './RecommendationCard.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Mirror of ChatInterface.jsx's PRIORITY_LABELS. TODO: extract into a shared
// module (e.g. client/src/components/AI/priorityHelpers.js) on a future cleanup
// pass. For now duplicated here since both components need it.
const PRIORITY_LABELS = {
  proximity_to_work: { emoji: '🏢', label: 'Near work' },
  schools: { emoji: '🏫', label: 'Schools' },
  safety: { emoji: '🛡️', label: 'Safety' },
  walkability: { emoji: '🚶', label: 'Walkable' },
  transit: { emoji: '🚇', label: 'Transit' },
  nightlife: { emoji: '🌃', label: 'Nightlife' },
  parks: { emoji: '🌳', label: 'Parks' },
  affordability: { emoji: '💰', label: 'Affordable' },
  budget: { emoji: '💰', label: 'Budget fit' },
  healthcare: { emoji: '🏥', label: 'Healthcare' },
  diversity: { emoji: '🌍', label: 'Diverse' },
  lifestyle_amenities: { emoji: '✨', label: 'Lifestyle' },
  coffee_culture: { emoji: '☕', label: 'Coffee' },
  fine_dining: { emoji: '🍷', label: 'Fine dining' },
  shopping: { emoji: '🛍️', label: 'Shopping' },
  fitness: { emoji: '💪', label: 'Fitness' },
  dog_friendly: { emoji: '🐕', label: 'Dog-friendly' },
  artsy: { emoji: '🎨', label: 'Arts' },
  indian_community: { emoji: '🪔', label: 'Indian community' },
  chinese_community: { emoji: '🥢', label: 'Chinese community' },
  korean_community: { emoji: '🇰🇷', label: 'Korean community' },
  japanese_community: { emoji: '🌸', label: 'Japanese community' },
  hispanic_community: { emoji: '🌶️', label: 'Hispanic community' },
  jewish_community: { emoji: '✡️', label: 'Jewish community' },
  middle_eastern_community: { emoji: '🕌', label: 'Middle Eastern' }
};

/**
 * Compute a neighborhood-specific metric string for a given priority key.
 * Returns a short suffix to append to the tag label (e.g. "10 min", "9.4/10",
 * "strong"). Returns null if no meaningful metric is available for this
 * priority/data combination — in which case the tag shows label only.
 */
const getPriorityMetric = (priorityKey, rawData) => {
  if (!rawData) return null;
  const q = rawData.qualitative || {};

  switch (priorityKey) {
    case 'proximity_to_work':
      return rawData.commuteTimeMin ? `${rawData.commuteTimeMin} min` : null;

    case 'schools':
      return rawData.schools?.averageRating != null
        ? `${rawData.schools.averageRating}/10`
        : null;

    case 'safety':
      return rawData.safety?.safetyScore != null
        ? `${rawData.safety.safetyScore}/100`
        : null;

    case 'walkability':
      return rawData.walkabilityScore != null
        ? `${rawData.walkabilityScore}/100`
        : null;

    case 'transit':
      return rawData.transitScore != null
        ? `${rawData.transitScore}/100`
        : null;

    case 'affordability':
    case 'budget':
      return rawData.avgRent ? `$${rawData.avgRent.toLocaleString()}/mo` : null;

    // Qualitative (community + lifestyle) — use score tier
    case 'indian_community':
    case 'chinese_community':
    case 'korean_community':
    case 'japanese_community':
    case 'hispanic_community':
    case 'jewish_community':
    case 'middle_eastern_community':
    case 'artsy':
    case 'nightlife':
    case 'coffee_culture':
    case 'fine_dining':
    case 'shopping':
    case 'fitness':
    case 'dog_friendly':
      const score = q[priorityKey];
      if (score == null) return null;
      if (score >= 70) return 'strong';
      if (score >= 40) return 'present';
      return 'limited';

    default:
      return null;
  }
};

/**
 * Priority-based tag derivation (preferred over threshold-based).
 * Shows exactly the priorities the user stated, with a per-neighborhood
 * metric. Each tag directly answers "how does this neighborhood perform
 * on a thing I said I care about?"
 */
const derivePriorityTags = (userPriorities, rawData) => {
  if (!Array.isArray(userPriorities) || userPriorities.length === 0) return [];

  return userPriorities.slice(0, 4).map((key) => {
    const known = PRIORITY_LABELS[key];
    if (!known) return null; // unknown priority → silently skip (same pattern as ChatInterface pills)
    return {
      key,
      emoji: known.emoji,
      label: known.label,
      metric: getPriorityMetric(key, rawData)
    };
  }).filter(Boolean);
};

/**
 * Threshold-based tag derivation (fallback when we don't have user priorities).
 * Preserved from the earlier implementation so cards still render sensibly
 * if someone uses this component without passing userPriorities.
 */
const deriveThresholdTags = (rawData, qualitative) => {
  if (!rawData) return [];
  const tags = [];
  if (rawData.schools?.averageRating >= 7) tags.push({ label: 'Good Schools' });
  // 'Very Safe' / 'Safe' tags removed 2026-10-16 — they keyed off the same
  // invented city-level number, so they were either absent for every candidate
  // in a city or present for every candidate in a city.
  if (rawData.walkabilityScore >= 75) tags.push({ label: 'Walkable' });
  if (qualitative?.indian_community >= 70) tags.push({ label: 'Indian community' });
  if (qualitative?.artsy >= 60) tags.push({ label: 'Artsy' });
  if (qualitative?.nightlife >= 60) tags.push({ label: 'Nightlife' });
  return tags.slice(0, 4);
};


const formatCommute = (min) => {
  if (!min && min !== 0) return '—';
  return `${min} min`;
};

const formatSchoolRating = (rating) => {
  if (!rating && rating !== 0) return '—';
  // Schools provider returns 0-10 scale; some legacy paths return 0-5 — handle both.
  if (rating > 10) return '—'; // sanity guard
  return `${Number(rating).toFixed(1)}/10`;
};

/** Match score color ramp — green/amber/red for quick visual scan. */
const getScoreColor = (score) => {
  if (score >= 75) return 'var(--status-success)';
  if (score >= 60) return '#F59E0B'; // amber
  return '#EF4444'; // red
};

/** Human-readable priority/category names for the score breakdown. */
const formatCategoryName = (key) => {
  if (!key) return '';
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

// ─── Component ────────────────────────────────────────────────────────────────

const RecommendationCard = ({
  recommendation,
  rank,
  userPriorities,
  sessionId,        // for feedback API
  searchContext,    // { city, state, matchedPersona, housingIntent }
  // Round 6 (LG feedback 2026-05-05): when AIChat passes onPanelOpen, the
  // Compare / Explore buttons render as <button>s that swap the right panel
  // in-place instead of navigating to the standalone /areas/:slug/* routes
  // (which would replace the whole AIChat page and lose the chat history).
  // If onPanelOpen is NOT provided, the buttons fall back to <Link> for
  // backward compat (e.g. if the card is ever embedded outside AIChat).
  onPanelOpen = null
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'up' | 'down' | null
  const [feedbackError, setFeedbackError] = useState(null);

  const {
    neighborhood,
    matchScore,
    headline,
    summary,
    keyHighlights,
    whyItFits,
    scoreBreakdown,
    rawData
  } = recommendation || {};

  const qualitative = rawData?.qualitative || {};
  const hasHero = Boolean(rawData?.photoUrl);

  // Priority-based tags if we know what the user asked for; threshold-based
  // fallback only if userPriorities wasn't passed (shouldn't happen in
  // production but keeps component robust in isolation).
  const tags = Array.isArray(userPriorities) && userPriorities.length > 0
    ? derivePriorityTags(userPriorities, rawData)
    : deriveThresholdTags(rawData, qualitative);

  const handleFeedback = async (value) => {
    // ── Optimistic UI ──
    // Update local state immediately so the click feels instant.
    // If the API call fails we revert and surface a small error message.
    const prevFeedback = feedback;
    const newFeedback = feedback === value ? null : value;
    setFeedback(newFeedback);
    setFeedbackError(null);

    // ── Skip API call gracefully if required context is missing ──
    // This shouldn't happen in production but keeps the card robust in
    // isolation (e.g., Storybook, dev fixtures, missing parent props).
    const city  = searchContext?.city;
    const state = searchContext?.state;
    if (!sessionId || !city || !state || !neighborhood) {
      console.warn('[RecommendationCard] Skipping feedback POST — missing context', {
        sessionId, city, state, neighborhood
      });
      return;
    }

    // ── Call API ──
    let result;
    if (newFeedback === null) {
      // Toggle-off → DELETE the existing record
      result = await clearRecommendationFeedback({
        sessionId,
        neighborhoodName: neighborhood
      });
    } else {
      // Up or down → POST upsert with rich context
      result = await submitRecommendationFeedback({
        sessionId,
        neighborhoodName: neighborhood,
        city,
        state,
        vote: newFeedback,
        recommendationRank: rank,
        matchScore: typeof matchScore === 'number' ? matchScore : null,
        matchedPersona: searchContext?.matchedPersona || null,
        userPriorities: Array.isArray(userPriorities) ? userPriorities : [],
        housingIntent: searchContext?.housingIntent || null
      });
    }

    // ── Revert on failure ──
    if (!result?.success) {
      console.error('[RecommendationCard] Feedback save failed:', result?.error);
      setFeedback(prevFeedback);
      setFeedbackError(result?.error || 'Could not save your vote. Please try again.');
    }
  };

  return (
    <div className="rec-card">
      {/* ─── Hero ─────────────────────────────────────── */}
      <div className="rec-hero">
        {hasHero ? (
          <img
            src={rawData.photoUrl}
            alt={`${neighborhood || 'Neighborhood'} hero`}
            className="rec-hero-img"
            loading="lazy"
          />
        ) : (
          <div className="rec-hero-placeholder" aria-hidden="true">
            <span className="rec-hero-placeholder-text">
              {neighborhood || 'Neighborhood'}
            </span>
          </div>
        )}
        <div className="rec-hero-overlay" aria-hidden="true" />

        <div className="rec-rank-badge" aria-label={`Rank ${rank}`}>#{rank}</div>

        <div className="rec-match-score">
          <div
            className="rec-match-number"
            style={{ color: getScoreColor(matchScore) }}
          >
            {matchScore}
          </div>
          <div className="rec-match-label">MATCH</div>
        </div>
      </div>

      {/* ─── Body ─────────────────────────────────────── */}
      <div className="rec-body">
        <h2 className="rec-name">{neighborhood}</h2>

        {summary && <p className="rec-description">{summary}</p>}

        {/* Pill tags — directly reflect user priorities with per-neighborhood metrics */}
        {tags.length > 0 && (
          <div className="rec-tags" role="group" aria-label="How this neighborhood matches your priorities">
            {tags.map((tag, idx) => {
              // tag can be either a plain string (legacy threshold tag) or
              // a structured object { key, emoji, label, metric }
              if (typeof tag === 'string') {
                return <span key={idx} className="rec-tag">{tag}</span>;
              }
              return (
                <span key={tag.key || idx} className="rec-tag">
                  {tag.emoji && <span className="rec-tag-emoji">{tag.emoji} </span>}
                  <span className="rec-tag-label">{tag.label}</span>
                  {tag.metric && (
                    <span className="rec-tag-metric"> · {tag.metric}</span>
                  )}
                </span>
              );
            })}
          </div>
        )}

        {/* Metric sections */}
        {rawData && (
          <>
            {/* Safety section REMOVED 2026-10-16.
                It displayed a "Crime Rate" label and a "Safety Rating /100"
                derived from a number the product does not actually have: the
                FBI API 404s, the city baseline is a hand-written lookup table
                (Seattle = 52, anything unknown = 50), and the per-neighbourhood
                variation came from a GPT prompt that scored by affluence. With
                that prompt deleted the figure is identical for every candidate
                in a city, so the section showed three cards reading the same
                invented number. Restore it only alongside real crime data —
                see LOCALGENIE_BACKLOG.md. */}

            <section className="rec-section">
              <h3 className="rec-section-title">Schools</h3>
              <div className="rec-metric-grid">
                <div className="rec-metric-card">
                  <div className="rec-metric-label">Average Rating</div>
                  <div className="rec-metric-value">
                    {formatSchoolRating(rawData.schools?.averageRating)}
                  </div>
                </div>
                {/* CHANGED 2026-10-16: was "Number of Schools" showing
                    schools.count, which is the provider's maxResultCount of 20
                    — the page size of the Google request, identical for every
                    neighborhood in every city. qualifyingCount is the real
                    figure: schools rated 3.0 or above with at least 10 reviews.
                    Hidden entirely when the provider could not supply it,
                    rather than falling back to a misleading 0. */}
                {rawData.schools?.qualifyingCount != null && (
                  <div className="rec-metric-card">
                    <div className="rec-metric-label">Rated Schools Nearby</div>
                    <div className="rec-metric-value">
                      {rawData.schools.qualifyingCount}
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className="rec-section">
              <h3 className="rec-section-title">
                {rawData.avgHomeValue > 0 ? 'Home Value' : 'Rent'}
              </h3>
              <div className="rec-metric-grid rec-metric-grid-single">
                <div className="rec-metric-card">
                  <div className="rec-metric-label">
                    {rawData.avgHomeValue > 0 ? 'Average Home Value' : 'Average Rent'}
                  </div>
                  <div className="rec-metric-value">
                    {rawData.avgHomeValue > 0
                      ? `$${rawData.avgHomeValue.toLocaleString()}`
                      : `$${(rawData.avgRent || 0).toLocaleString()}`}
                    {!(rawData.avgHomeValue > 0) && (
                      <span className="rec-metric-unit">/mo</span>
                    )}
                  </div>
                  {/* ADDED 2026-10-18: over-budget disclosure. A stated budget
                      is now a hard filter (AnalysisAgent.applyBudgetConstraint),
                      so an over-budget card only appears when fewer than three
                      neighborhoods qualified. When it does appear, say so
                      plainly rather than letting the match score imply it fits. */}
                  {recommendation?.budgetStatus
                    && recommendation.budgetStatus.withinBudget === false && (
                    <div className="rec-metric-warning">
                      {recommendation.budgetStatus.pctOver}% above your $
                      {recommendation.budgetStatus.budget.toLocaleString()} budget
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="rec-section">
              <h3 className="rec-section-title">Commute</h3>
              <div className="rec-metric-grid">
                {/* CHANGED 2026-10-16: this always read "To Workplace", even
                    when the user never gave one. With no workplace the tool
                    falls back to `dest_lat: cityCoords.lat // Default to city
                    center`, so the figure is the commute downtown.
                    distanceToWorkplace is null in exactly that case, which is
                    the signal used here. */}
                <div className="rec-metric-card">
                  <div className="rec-metric-label">
                    {rawData.distanceToWorkplace != null
                      ? 'To Workplace'
                      : 'To City Centre'}
                  </div>
                  <div className="rec-metric-value">
                    {formatCommute(rawData.commuteTimeMin)}
                  </div>
                </div>
                <div className="rec-metric-card">
                  <div className="rec-metric-label">Walkability</div>
                  <div className="rec-metric-value">
                    {rawData.walkabilityScore ?? '—'}
                    {rawData.walkabilityScore != null && (
                      <span className="rec-metric-unit">/100</span>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        {/* "What residents say" section (added 2026-04-26 Sunday Week 2 RAG build).
            Only renders when reviewMatch is populated (i.e., RAG retrieval found
            ≥2 reviews for this neighborhood matching the user's priorities).
            For non-seeded cities, RAG retrieval returns null and this whole
            block is absent — graceful fall-through, no empty state. */}
        {rawData?.reviewMatch?.topQuote && (
          <section className="rec-reviews">
            <h4 className="rec-reviews-title">What residents say</h4>
            <blockquote className="rec-review-quote">
              <p>"{rawData.reviewMatch.topQuote.text}"</p>
              <footer className="rec-review-attribution">
                — {(rawData.reviewMatch.topQuote.reviewerType || 'anonymous').replace(/^./, c => c.toUpperCase())}
                {rawData.reviewMatch.reviewCount > 1 && (
                  <span className="rec-review-count">
                    {' · '}{rawData.reviewMatch.reviewCount} review{rawData.reviewMatch.reviewCount === 1 ? '' : 's'} of {neighborhood}
                  </span>
                )}
              </footer>
            </blockquote>
          </section>
        )}

        {/* Feedback buttons (frontend state only for now) */}
        <div className="rec-feedback">
          <span className="rec-feedback-prompt">Was this helpful?</span>
          <button
            type="button"
            className={`rec-thumb ${feedback === 'up' ? 'rec-thumb-active' : ''}`}
            onClick={() => handleFeedback('up')}
            aria-label="Thumbs up"
            aria-pressed={feedback === 'up'}
          >
            <span role="img" aria-hidden="true">👍</span>
          </button>
          <button
            type="button"
            className={`rec-thumb ${feedback === 'down' ? 'rec-thumb-active' : ''}`}
            onClick={() => handleFeedback('down')}
            aria-label="Thumbs down"
            aria-pressed={feedback === 'down'}
          >
            <span role="img" aria-hidden="true">👎</span>
          </button>
          {feedbackError && (
            <span className="rec-feedback-error" role="alert">
              {feedbackError}
            </span>
          )}
        </div>

        {/* Per-card share-review deep-link (added 2026-04-25).
            Only renders when we have enough context to pre-fill the form.
            URL params consumed by SubmitReview.jsx via useSearchParams. */}
        {neighborhood && searchContext?.city && searchContext?.state && (
          <div className="rec-share-link-wrap">
            <Link
              to={`/submit-review?state=${encodeURIComponent(searchContext.state)}&city=${encodeURIComponent(searchContext.city)}&neighborhood=${encodeURIComponent(neighborhood)}`}
              className="rec-share-link"
            >
              Lived in {neighborhood}? Share your experience →
            </Link>
          </div>
        )}

        {/* Action buttons. Round 6 (2026-05-05): if AIChat provides onPanelOpen,
            buttons swap the right panel in-place (chat preserved on left).
            Fallback: legacy <Link> navigation to standalone /areas/:slug/* routes. */}
        {neighborhood && searchContext?.city && searchContext?.state && (() => {
          // Shared payload for both render paths (in-panel callback + standalone link).
          const payload = {
            item: {
              title: neighborhood,
              name: neighborhood,
              city: searchContext.city,
              region: searchContext.state,
              state: searchContext.state,
              image: rawData?.photoUrl || null,
              id: neighborhood,
            },
            city: searchContext.city,
            region: searchContext.state,
            image: rawData?.photoUrl || null,
            heroImage: rawData?.photoUrl || null,
            preserveChat: true,
          };
          const slug = encodeURIComponent(neighborhood.toLowerCase().replace(/\s+/g, '-'));

          return (
            <div className="rec-action-buttons">
              {onPanelOpen ? (
                <button
                  type="button"
                  onClick={() => onPanelOpen('compare', payload)}
                  className="rec-action-btn rec-compare-btn"
                >
                  ⚖️ Compare Areas
                </button>
              ) : (
                <Link
                  to={`/areas/${slug}/compare`}
                  state={payload}
                  className="rec-action-btn rec-compare-btn"
                >
                  ⚖️ Compare Areas
                </Link>
              )}
              {onPanelOpen ? (
                <button
                  type="button"
                  onClick={() => onPanelOpen('explore', payload)}
                  className="rec-action-btn rec-explore-btn"
                >
                  🔍 Explore More
                </button>
              ) : (
                <Link
                  to={`/areas/${slug}/explore`}
                  state={payload}
                  className="rec-action-btn rec-explore-btn"
                >
                  🔍 Explore More
                </Link>
              )}
            </div>
          );
        })()}

        {/* Expandable "Why this neighborhood?" section */}
        {(keyHighlights?.length > 0 || whyItFits || scoreBreakdown) && (
          <>
            <button
              type="button"
              className="rec-expand-btn"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
            >
              {isExpanded ? 'Show less' : 'Why this neighborhood?'}
            </button>

            {isExpanded && (
              <div className="rec-expanded">
                {headline && <p className="rec-headline">{headline}</p>}

                {keyHighlights && keyHighlights.length > 0 && (
                  <div className="rec-detail-section">
                    <h4 className="rec-detail-title">Highlights</h4>
                    <ul className="rec-highlights">
                      {keyHighlights.map((h, idx) => (
                        <li key={idx}>{h}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {whyItFits && (
                  <div className="rec-detail-section">
                    <h4 className="rec-detail-title">Why it fits you</h4>
                    <p>{whyItFits}</p>
                  </div>
                )}

                {scoreBreakdown && (
                  <div className="rec-detail-section">
                    <h4 className="rec-detail-title">Score breakdown</h4>
                    <div className="rec-score-breakdown">
                      {Object.entries(scoreBreakdown)
                        .filter(([key, value]) => value > 0 && key !== 'overall')
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 6)
                        .map(([category, score]) => (
                          <div key={category} className="rec-score-item">
                            <div className="rec-score-item-header">
                              <span className="rec-category-name">
                                {formatCategoryName(category)}
                              </span>
                              <span className="rec-category-score">
                                {score}/100
                              </span>
                            </div>
                            <div className="rec-score-bar">
                              <div
                                className="rec-score-bar-fill"
                                style={{
                                  width: `${Math.min(score, 100)}%`,
                                  backgroundColor: getScoreColor(score)
                                }}
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RecommendationCard;
